<template>
  <div class="dv-wrapper">
    <div class="dv-header">
      <img src="@/assets/img/mydessk-black.svg" alt="Mydessk" class="dv-logo" />
    </div>

    <div class="dv-card" v-if="loading">
      <div class="dv-loading">
        <i class="el-icon-loading"></i>
        <p>Verificando documento...</p>
      </div>
    </div>

    <div class="dv-card" v-else-if="error">
      <div class="dv-status dv-status--invalid">
        <i class="el-icon-circle-close"></i>
        <h2>Documento no encontrado</h2>
        <p>
          El código de verificación no corresponde a ningún documento en nuestra
          plataforma.
        </p>
      </div>
    </div>

    <template v-else-if="doc">
      <div class="dv-card">
        <div class="dv-status dv-status--valid">
          <i class="el-icon-circle-check"></i>
          <h2>Documento verificado</h2>
          <p>
            Este documento es auténtico y fue firmado digitalmente a través de
            Mydessk.
          </p>
        </div>

        <div class="dv-section">
          <h3 class="dv-section__title">Información del documento</h3>
          <div class="dv-row">
            <span class="dv-label">Nombre</span>
            <span class="dv-value">{{ doc.document_name }}</span>
          </div>
          <div class="dv-row">
            <span class="dv-label">Tipo</span>
            <span class="dv-value">{{ doc.document_type }}</span>
          </div>
          <div class="dv-row">
            <span class="dv-label">Estado</span>
            <span class="dv-badge dv-badge--signed">{{ doc.status }}</span>
          </div>
          <div class="dv-row" v-if="doc.owner_signed_at">
            <span class="dv-label">Firmado el</span>
            <span class="dv-value">{{ formatDate(doc.owner_signed_at) }}</span>
          </div>
          <div class="dv-row">
            <span class="dv-label">Hash SHA-256</span>
            <span class="dv-value dv-value--mono">{{ doc.file_hash }}</span>
          </div>
        </div>

        <div class="dv-section" v-if="doc.owner">
          <h3 class="dv-section__title">Emisor</h3>
          <div class="dv-row">
            <span class="dv-label">Empresa</span>
            <span class="dv-value">{{ doc.owner.business_name }}</span>
          </div>
          <div class="dv-row" v-if="doc.owner.ruc">
            <span class="dv-label">RUC</span>
            <span class="dv-value dv-value--mono">{{ doc.owner.ruc }}</span>
          </div>
        </div>

        <div class="dv-section" v-if="doc.signers && doc.signers.length">
          <h3 class="dv-section__title">Firmantes</h3>
          <div v-for="(signer, i) in doc.signers" :key="i" class="dv-signer">
            <div class="dv-signer__name">{{ signer.name }}</div>
            <div class="dv-signer__meta" v-if="signer.signed_at">
              Firmado el {{ formatDate(signer.signed_at) }}
            </div>
            <div class="dv-signer__meta dv-signer__meta--pending" v-else>
              Firma pendiente
            </div>
          </div>
        </div>
      </div>

      <p class="dv-footer">
        Verificado por <strong>Mydessk</strong> · {{ verifiedAt }}
      </p>
    </template>
  </div>
</template>

<script>
import Api from "@/config/Api";

export default {
  name: "DocumentVerify",
  data() {
    return {
      loading: true,
      error: false,
      doc: null,
      verifiedAt: ""
    };
  },
  mounted() {
    this.verify();
    this.verifiedAt = new Date().toLocaleString("es-EC");
  },
  methods: {
    async verify() {
      const hash = this.$route.params.hash;
      try {
        const res = await Api().get(`/docs/verify/${hash}`);
        if (res.data && res.data.valid) {
          this.doc = res.data;
        } else {
          this.error = true;
        }
      } catch {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleString("es-EC", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #2d2b55;
$accent: #3ecfb4;
$green: #1d9e75;
$red: #f56c6c;
$border: #eef0f8;
$radius: 16px;

.dv-wrapper {
  min-height: 100vh;
  background: #f4f5fb;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px 48px;
}

.dv-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 32px;
}

.dv-logo {
  height: 36px;
}

.dv-card {
  width: 100%;
  max-width: 560px;
  background: #fff;
  border: 1.5px solid $border;
  border-radius: $radius;
  padding: 32px;
  margin-bottom: 16px;
}

.dv-loading {
  text-align: center;
  padding: 24px 0;
  color: $primary;

  i {
    font-size: 36px;
    color: $accent;
    display: block;
    margin-bottom: 12px;
  }

  p {
    font-size: 14px;
    color: #888;
    margin: 0;
  }
}

.dv-status {
  text-align: center;
  padding-bottom: 24px;
  margin-bottom: 24px;
  border-bottom: 1.5px solid $border;

  i {
    font-size: 48px;
    display: block;
    margin-bottom: 12px;
  }

  h2 {
    font-size: 20px;
    font-weight: 700;
    color: $primary;
    margin: 0 0 8px;
  }

  p {
    font-size: 14px;
    color: #888;
    margin: 0;
    line-height: 1.5;
  }

  &--valid i {
    color: $green;
  }
  &--invalid {
    border-bottom: none;
    margin-bottom: 0;
    i {
      color: $red;
    }
  }
}

.dv-section {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }

  &__title {
    font-size: 11px;
    font-weight: 700;
    color: #aaa;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 12px;
  }
}

.dv-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid $border;
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }
}

.dv-label {
  width: 110px;
  flex-shrink: 0;
  color: #aaa;
  font-weight: 600;
  font-size: 12px;
  padding-top: 1px;
}

.dv-value {
  color: $primary;
  font-weight: 500;
  word-break: break-all;

  &--mono {
    font-family: monospace;
    font-size: 11px;
    color: #666;
  }
}

.dv-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;

  &--signed {
    background: rgba($green, 0.1);
    color: $green;
  }
}

.dv-signer {
  padding: 10px 0;
  border-bottom: 1px solid $border;

  &:last-child {
    border-bottom: none;
  }

  &__name {
    font-size: 14px;
    font-weight: 600;
    color: $primary;
    margin-bottom: 2px;
  }

  &__meta {
    font-size: 12px;
    color: #888;

    &--pending {
      color: #f0a500;
    }
  }
}

.dv-footer {
  font-size: 12px;
  color: #aaa;
  text-align: center;
  margin: 0;
}
</style>
